const CENTER = '50%'
const ATTACKING_MIDFIELD_POSITION = '62%'
const DEFENDING_MIDFIELD_POSITION = '40%'

const ATTACKING_FORWARD_POSITION = '72%' // Moved higher to add space between ATT and MID

const POSITION_GK = { bottom: '10%', left: CENTER, position: 'GK' }

const POSITION_LB = { bottom: '30%', left: '12%', position: 'LB' }
const POSITION_LCB = { bottom: '24%', left: '30%', position: 'LCB' }
const POSITION_CCB = { bottom: '24%', left: CENTER, position: 'CCB' }
const POSITION_RCB = { bottom: '24%', left: '70%', position: 'RCB' }
const POSITION_RB = { bottom: '30%', left: '88%', position: 'RB' }

const POSITION_LM = { bottom: '56%', left: '10%', position: 'LM' }
const POSITION_LCM = { bottom: '50%', left: '28%', position: 'LCM' }
const POSITION_LDM = { bottom: DEFENDING_MIDFIELD_POSITION, left: '30%', position: 'LDM' }
const POSITION_CDM = { bottom: DEFENDING_MIDFIELD_POSITION, left: CENTER, position: 'CDM' }
const POSITION_LAM = { bottom: ATTACKING_MIDFIELD_POSITION, left: '30%', position: 'LAM' }
const POSITION_CM = { bottom: '46%', left: CENTER, position: 'CM' }
const POSITION_CAM = { bottom: ATTACKING_MIDFIELD_POSITION, left: CENTER, position: 'CAM' }
const POSITION_RAM = { bottom: ATTACKING_MIDFIELD_POSITION, left: '70%', position: 'RAM' }
const POSITION_RDM = { bottom: DEFENDING_MIDFIELD_POSITION, left: '70%', position: 'RDM' }
const POSITION_RCM = { bottom: '50%', left: '72%', position: 'RCM' }
const POSITION_RM = { bottom: '56%', left: '90%', position: 'RM' }

const POSITION_LW = { bottom: ATTACKING_FORWARD_POSITION, left: '20%', position: 'LW' } // Moved in from 12% to stay on pitch
const POSITION_LS = { bottom: ATTACKING_FORWARD_POSITION, left: '30%', position: 'LS' }
const POSITION_CF = { bottom: ATTACKING_FORWARD_POSITION, left: CENTER, position: 'CF' }
const POSITION_RS = { bottom: ATTACKING_FORWARD_POSITION, left: '70%', position: 'RS' }
const POSITION_RW = { bottom: ATTACKING_FORWARD_POSITION, left: '80%', position: 'RW' } // Moved in from 88% to stay on pitch

export const positionMap: { [key: string]: string } = {
    GK: 'Goalkeeper',
    LCB: 'Left Centerback',
    LB: 'Left Back',
    CB: 'Centerback',
    CCB: 'Center Centerback',
    RCB: 'Right Centerback',
    RB: 'Right Back',
    LWB: 'Left Wing Back',
    RWB: 'Right Wing Back',
    LM: 'Left Midfield',
    LCM: 'Left Center Midfield',
    CM: 'Center Midfield',
    LDM: 'Left Defensive Midfield',
    CDM: 'Center Defensive Midfield',
    RDM: 'Right Defensive Midfield',
    LAM: 'Left Attacking Midfield',
    CAM: 'Central Attacking Midfield',
    RAM: 'Right Attacking Midfield',
    RCM: 'Right Center Midfield',
    RM: 'Right Midfield',
    ST: 'Striker',
    CF: 'Central Forward',
    LS: 'Left Striker',
    LW: 'Left Wing',
    RS: 'Right Striker',
    RW: 'Right Wing'
}

export const GOALKEEPER = 'Goalkeeper'

export const DEFENDERS = [
    'Left Centerback',
    'Left Back',
    'Centerback',
    'Center Centerback',
    'Right Centerback',
    'Right Back',
    'Left Wing Back',
    'Right Wing Back'
]

export const MIDFIELDERS = [
    'Left Midfield',
    'Left Center Midfield',
    'Center Midfield',
    'Left Defensive Midfield',
    'Center Defensive Midfield',
    'Right Defensive Midfield',
    'Left Attacking Midfield',
    'Central Attacking Midfield',
    'Right Attacking Midfield',
    'Right Center Midfield',
    'Right Midfield'
]

export const ATTACKERS = ['Striker', 'Central Forward', 'Left Striker', 'Left Wing', 'Right Striker', 'Right Wing', 'Left Forward', 'Right Forward']

// Updated for 3-5-2 formation using EA's simplified position categories (GK, DEF, MID, ST)
export const TOTW_POSITION_GROUPS = [
    { name: 'Goalkeeper', positions: [GOALKEEPER], maxPlayers: 1 },
    { name: 'Defender', positions: DEFENDERS, maxPlayers: 3 }, // 3 defenders for 3-5-2
    { name: 'Midfielder', positions: MIDFIELDERS, maxPlayers: 5 }, // 5 midfielders for 3-5-2
    { name: 'Attacker', positions: ATTACKERS, maxPlayers: 2 } // 2 attackers for 3-5-2
]

/** TOTW 3-5-2 slot types: 1 GK, 3 CB, 2 CDM, 1 CAM, 2 Winger, 2 Striker. Used for lineup-based TOTW selection. */
export const TOTW_352_SLOTS = [
    { name: 'Goalkeeper', maxPlayers: 1 },
    { name: 'CB', maxPlayers: 3 },
    { name: 'CDM', maxPlayers: 2 },
    { name: 'CAM', maxPlayers: 1 },
    { name: 'Winger', maxPlayers: 2 },
    { name: 'Striker', maxPlayers: 2 }
] as const

export type TOTW352SlotName = (typeof TOTW_352_SLOTS)[number]['name']

/**
 * Maps a lineup position (code or full name) to one or more TOTW 3-5-2 slot types.
 * Some positions are "profiled against both" (e.g. LAM/RAM → CAM and Winger); player is only used once when filling XI.
 */
export function mapLineupPositionToTOTWSlots(position: string): TOTW352SlotName[] {
    if (!position || !position.trim()) return []
    // Normalize: lineup stores full names; support both code (e.g. LW) and full name (e.g. Left Wing)
    const fromCode = convertFullNameToPositionCode(position)
    const code = (fromCode || (position.length <= 4 ? position : '')).toUpperCase().trim()
    const fullLower = position.toLowerCase().trim()

    // Goalkeeper
    if (code === 'GK' || fullLower === 'goalkeeper' || fullLower.includes('keeper')) return ['Goalkeeper']

    // CB – center backs only
    if (['CB', 'LCB', 'RCB', 'CCB'].includes(code) || fullLower.includes('centerback') || fullLower.includes('centreback')) return ['CB']

    // CDM
    if (['CDM', 'LDM', 'RDM'].includes(code) || fullLower.includes('defensive midfield')) return ['CDM']
    // Central mids – profile against both CDM and CAM (used only once when filling XI)
    if (['LCM', 'RCM', 'CM'].includes(code) || fullLower.includes('left center mid') || fullLower.includes('right center mid') || fullLower === 'center midfield') return ['CDM', 'CAM']

    // CAM – profile against both CAM and Winger
    if (['CAM', 'LAM', 'RAM'].includes(code) || fullLower.includes('attacking midfield')) return ['CAM', 'Winger']

    // Winger – wide
    if (['RM', 'LM', 'RW', 'LW', 'RB', 'LB'].includes(code) || fullLower.includes('left wing') || fullLower.includes('right wing') || fullLower.includes('left midfield') || fullLower.includes('right midfield') || fullLower.includes('left back') || fullLower.includes('right back')) return ['Winger']
    // Wing-backs – profile against both CB and Winger
    if (['LWB', 'RWB'].includes(code) || fullLower.includes('wing back')) return ['CB', 'Winger']

    // Striker
    if (['ST', 'CF', 'LS', 'RS'].includes(code) || fullLower.includes('striker') || (fullLower.includes('forward') && !fullLower.includes('wing'))) return ['Striker']

    // EA fallback: when no lineup is submitted, EA sends generic "defender" / "midfielder"
    // Map them so TOTW can still show 11 players until lineups are available
    if (fullLower === 'defender') return ['CB']
    if (fullLower === 'midfielder') return ['CDM', 'CAM', 'Winger']

    return []
}

export const convertPositionCodeToFullName = (positionCode: string) => {
    return positionMap[positionCode] || ''
}

export const convertFullNameToPositionCode = (fullName: string) => {
    const reversePositionMap: { [key: string]: string } = {}
    for (const code in positionMap) {
        if (positionMap.hasOwnProperty(code)) {
            const fullName = positionMap[code]
            reversePositionMap[fullName] = code
        }
    }

    return reversePositionMap[fullName] || ''
}

export const formationList = [
    {
        formation: '3-1-4-2',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LCB, POSITION_CCB, POSITION_RCB] },
            { locations: [POSITION_LM, POSITION_LCM, POSITION_CDM, POSITION_RCM, POSITION_RM] },
            { locations: [POSITION_LS, POSITION_RS] }
        ]
    },
    {
        formation: '3-4-1-2',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LCB, POSITION_CCB, POSITION_RCB] },
            { locations: [POSITION_LM, POSITION_LCM, POSITION_RCM, POSITION_RM] },
            { locations: [POSITION_CAM] },
            { locations: [POSITION_LS, POSITION_RS] }
        ]
    },
    {
        formation: '3-4-2-1',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LCB, POSITION_CCB, POSITION_RCB] },
            { locations: [POSITION_LM, POSITION_LDM, POSITION_LAM, POSITION_RAM, POSITION_RDM, POSITION_RM] },
            { locations: [POSITION_CF] }
        ]
    },
    {
        formation: '3-4-3 (Diamond)',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LCB, POSITION_CCB, POSITION_RCB] },
            { locations: [POSITION_LCM, POSITION_CDM, POSITION_CAM, POSITION_RCM] },
            { locations: [POSITION_LS, POSITION_CF, POSITION_RS] }
        ]
    },
    {
        formation: '3-4-3 (Flat)',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LCB, POSITION_CCB, POSITION_RCB] },
            { locations: [POSITION_LM, POSITION_LCM, POSITION_RCM, POSITION_RM] },
            { locations: [POSITION_LS, POSITION_CF, POSITION_RS] }
        ]
    },
    {
        formation: '3-5-1-1',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LCB, POSITION_CCB, POSITION_RCB] },
            { locations: [POSITION_LM, POSITION_LCM, POSITION_CDM, POSITION_CAM, POSITION_RCM, POSITION_RM] },
            { locations: [POSITION_CF] }
        ]
    },
    {
        formation: '3-5-2',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LCB, POSITION_CCB, POSITION_RCB] },
            { locations: [POSITION_LM, POSITION_LDM, POSITION_CAM, POSITION_RDM, POSITION_RM] },
            { locations: [POSITION_LS, POSITION_RS] }
        ]
    },
    {
        formation: '4-1-2-1-2 (Narrow)',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LCM, POSITION_CDM, POSITION_CAM, POSITION_RCM] },
            { locations: [POSITION_LS, POSITION_RS] }
        ]
    },
    {
        formation: '4-1-2-1-2 (Wide)',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LM, POSITION_CDM, POSITION_CAM, POSITION_RM] },
            { locations: [POSITION_LS, POSITION_RS] }
        ]
    },
    {
        formation: '4-1-3-2',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_RCB, POSITION_RB] },
            //{ locations: [POSITION_LAM, POSITION_CDM, POSITION_CAM, POSITION_RAM] },
            { locations: [POSITION_LCM, POSITION_CDM, POSITION_CAM, POSITION_RCM] },
            { locations: [POSITION_LS, POSITION_RS] }
        ]
    },
    {
        formation: '4-1-4-1',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LM, POSITION_LCM, POSITION_CDM, POSITION_RCM, POSITION_RM] },
            { locations: [POSITION_CF] }
        ]
    },
    //'4-2-1-3', // TODO missing svg
    {
        formation: '4-2-2-2',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LDM, POSITION_LAM, POSITION_RAM, POSITION_RDM] },
            { locations: [POSITION_LS, POSITION_RS] }
        ]
    },
    {
        formation: '4-2-3-1 (Wide)',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LM, POSITION_LDM, POSITION_CAM, POSITION_RDM, POSITION_RM] },
            { locations: [POSITION_CF] }
        ]
    },
    {
        formation: '4-2-3-1 (Narrow)',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LCM, POSITION_LDM, POSITION_CAM, POSITION_RDM, POSITION_RCM] },
            { locations: [POSITION_CF] }
        ]
    },
    {
        formation: '4-2-4',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LCM, POSITION_RCM] },
            { locations: [POSITION_LW, POSITION_LS, POSITION_RS, POSITION_RW] }
        ]
    },
    {
        formation: '4-3-1-2',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LCM, POSITION_CM, POSITION_CAM, POSITION_RCM] },
            { locations: [POSITION_LS, POSITION_RS] }
        ]
    },
    {
        formation: '4-3-2-1',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LCM, POSITION_LAM, POSITION_CM, POSITION_RAM, POSITION_RCM] },
            { locations: [POSITION_CF] }
        ]
    },
    {
        formation: '4-3-3 (False 9)',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LCM, POSITION_CDM, POSITION_RCM] },
            { locations: [POSITION_LW, POSITION_CF, POSITION_RW] }
        ]
    },
    {
        formation: '4-3-3 (Attack)',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LCM, POSITION_CAM, POSITION_RCM] },
            { locations: [POSITION_LW, POSITION_CF, POSITION_RW] }
        ]
    },
    {
        formation: '4-3-3 (Defend)',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LDM, POSITION_CDM, POSITION_RDM] },
            { locations: [POSITION_LW, POSITION_CF, POSITION_RW] }
        ]
    },
    {
        formation: '4-3-3 (Holding)',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LCM, POSITION_CDM, POSITION_RCM] },
            { locations: [POSITION_LW, POSITION_CF, POSITION_RW] }
        ]
    },
    {
        formation: '4-3-3 (Flat)',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LCM, POSITION_CM, POSITION_RCM] },
            { locations: [POSITION_LW, POSITION_CF, POSITION_RW] }
        ]
    },
    {
        formation: '4-4-1-1 (Attack)',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LM, POSITION_LCM, POSITION_CAM, POSITION_RCM, POSITION_RM] },
            { locations: [POSITION_CF] }
        ]
    },
    {
        formation: '4-4-1-1 (Midfield)',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LM, POSITION_LDM, POSITION_CAM, POSITION_RDM, POSITION_RM] },
            { locations: [POSITION_CF] }
        ]
    },
    {
        formation: '4-4-2 (Holding)',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LM, POSITION_LDM, POSITION_RDM, POSITION_RM] },
            { locations: [POSITION_LS, POSITION_RS] }
        ]
    },
    {
        formation: '4-4-2 (Flat)',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LM, POSITION_LDM, POSITION_RDM, POSITION_RM] },
            { locations: [POSITION_LS, POSITION_RS] }
        ]
    },
    {
        formation: '4-5-1 (Attack)',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LM, POSITION_LAM, POSITION_CDM, POSITION_RAM, POSITION_RM] },
            { locations: [POSITION_CF] }
        ]
    },
    {
        formation: '4-5-1 (Flat)',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LM, POSITION_LCM, POSITION_CM, POSITION_RCM, POSITION_RM] },
            { locations: [POSITION_CF] }
        ]
    },
    {
        formation: '5-2-1-2',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_CCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LCM, POSITION_CAM, POSITION_RCM] },
            { locations: [POSITION_LS, POSITION_RS] }
        ]
    },
    {
        formation: '5-2-2-1',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_CCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LAM, POSITION_LDM, POSITION_RDM, POSITION_RAM] },
            { locations: [POSITION_CF] }
        ]
    },
    //'5-3-2', // check on fifa
    {
        formation: '5-3-2 (Holding)',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_CCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LDM, POSITION_CM, POSITION_RDM] },
            { locations: [POSITION_LS, POSITION_RS] }
        ]
    },
    {
        formation: '5-4-1 (Flat)',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_CCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LM, POSITION_LCM, POSITION_RCM, POSITION_RM] },
            { locations: [POSITION_CF] }
        ]
    },
    {
        formation: '5-4-1 (Diamond)',
        layout: [
            { locations: [POSITION_GK] },
            { locations: [POSITION_LB, POSITION_LCB, POSITION_CCB, POSITION_RCB, POSITION_RB] },
            { locations: [POSITION_LCM, POSITION_CDM, POSITION_CAM, POSITION_RCM] },
            { locations: [POSITION_CF] }
        ]
    }
]
